import { getToken } from './utils/tokenStorage';
import { sendOrQueue } from './utils/offlineQueue';
import { lazyRetry } from './utils/lazyWithRetry';
import { BUILD_ID } from './build-id';
import { APP_VERSION } from './constants/appVersion';
import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';

// Print build id once so we can confirm in browser console which bundle
// is actually running on a user's device (especially useful when a CDN
// pins a stale JS file at the edge).
 
console.log('[App] BUILD_ID:', BUILD_ID);
import { Toaster } from './components/ui/sonner';
// Eager-loaded: public/SEO routes that anonymous visitors land on.
// Keeping these in the initial bundle ensures a small fast first paint
// for the highest-traffic surfaces (LCP-critical).
import LandingPage from './components/LandingPage';
// Route-level pages moved off the landing bundle. Lighthouse (iter177)
// flagged 118 KiB of unused JS on `/` from these modules — they're only
// reached via a deliberate navigation (`/calculators`, `/fatai`,
// `/exercises`, `/trainers`, etc.), so lazy-loading recovers that whole
// wedge for cold LCP. Googlebot renders JS with a >5s budget, so the
// Suspense fallback still lets the SEO routes index cleanly.
const CalculatorsPage = lazyRetry(() => import('./components/CalculatorsPage'));
const MuscularPotentialPage = lazyRetry(() => import('./components/MuscularPotentialPage'));
const FatAIPage = lazyRetry(() => import('./components/FatAIPage'));
const PrivacyPolicyPage = lazyRetry(() => import('./components/legal/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const MedicalDisclaimerPage = lazyRetry(() => import('./components/legal/MedicalDisclaimerPage').then(m => ({ default: m.MedicalDisclaimerPage })));
const ExercisesIndex = lazyRetry(() => import('./components/exercises/ExercisesIndex').then(m => ({ default: m.ExercisesIndex })));
const ExercisePage = lazyRetry(() => import('./components/exercises/ExercisePage').then(m => ({ default: m.ExercisePage })));
const TrainersIndex = lazyRetry(() => import('./components/trainers/TrainersIndex').then(m => ({ default: m.TrainersIndex })));
const TrainerProfilePage = lazyRetry(() => import('./components/trainers/TrainerProfilePage').then(m => ({ default: m.TrainerProfilePage })));
const HallOfFamePage = lazyRetry(() => import('./components/trainers/HallOfFamePage').then(m => ({ default: m.HallOfFamePage })));

// Lazy-loaded: in-app surfaces only ever reached by authenticated users
// or after a deliberate navigation. Each becomes its own webpack chunk
// so a first-time visitor on the landing page never downloads them.
// Lighthouse audit (2026-05) showed the unsplit bundle was 1.1 MB with
// >1 MB of unused JS — split routes recover that for cold-start LCP.
const TrainerClientDashboard = lazyRetry(() => import('./components/trainer-dashboard/TrainerClientDashboard').then(m => ({ default: m.TrainerClientDashboard })));
const IntakeFormPage = lazyRetry(() => import('./components/intake/IntakeFormPage').then(m => ({ default: m.IntakeFormPage })));
const OnboardingFlow = lazyRetry(() => import('./components/onboarding/OnboardingFlow'));
const TimerScreen = lazyRetry(() => import('./components/TimerScreen'));
const SetupScreen = lazyRetry(() => import('./components/SetupScreen'));
const HistoryScreen = lazyRetry(() => import('./components/HistoryScreen'));
const WalkingSetup = lazyRetry(() => import('./components/WalkingSetup'));
const WalkingTimer = lazyRetry(() => import('./components/WalkingTimer'));
const GymNotebook = lazyRetry(() => import('./components/GymNotebook'));
const VoiceRecorder = lazyRetry(() => import('./components/VoiceRecorder'));
const AdminPanel = lazyRetry(() => import('./components/AdminPanel'));
// Blog routes: the post body components total ~31KB of JSX that Lighthouse
// (iter 142) flagged as wasted-JS on the landing-page main bundle. Lazy
// loading moves them to a /blog-only chunk; the landing page now ships
// only meta cards via `BLOG_POST_METAS` (see `blogPostsMeta.ts`).
const BlogIndex = lazyRetry(() => import('./components/blog/BlogIndex'));
const BlogPost = lazyRetry(() => import('./components/blog/BlogPost'));
// Marketing subpages — feature breakdowns for /workout, /nutrition,
// /ai-planner-and-analyzer. SEO-critical so they get their own chunks
// (small ~250-line files) and aren't shipped in the landing bundle.
const WorkoutSubpage = lazyRetry(() => import('./components/landing/WorkoutSubpage'));
const NutritionSubpage = lazyRetry(() => import('./components/landing/NutritionSubpage'));
const AiPlannerSubpage = lazyRetry(() => import('./components/landing/AiPlannerSubpage'));

import BackgroundTheme from './components/BackgroundTheme';
import { ThemeProvider } from './components/ThemeProvider';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SyncProvider } from './contexts/SyncContext';
import { ExerciseProvider } from './contexts/ExerciseContext';
import { CookieBanner } from './components/CookieBanner';
import { OfflineQueueBanner } from './components/OfflineQueueBanner';
import { Volume2, VolumeX, History, Home, Palette, Mic, ArrowLeft, CalendarDays, Footprints, BarChart3 } from 'lucide-react';
import { Button } from './components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import api from './services/api';
import { toast } from 'sonner';
import './App.css';
import { clarityPause, clarityResume } from './utils/clarity';
import { BootDots } from './components/ui/BootDots';
import { usePublicPageView } from './hooks/useAnalytics';
import { appPathname } from './utils/appPath';

/**
 * Lightweight fallback shown while a lazy-loaded route's chunk downloads.
 * Same 3-dot animation as the boot splash so the loader never changes shape.
 */
function RouteLoadingFallback() {
  return <BootDots testId="route-loading-fallback" />;
}

const API_URL = '';

// Types
interface WalkingConfig {
  quickWalkingTime: number;
  slowWalkingTime: number;
  sets?: number;
  quickWalkingSets?: number;
  slowWalkingSets?: number;
}

interface WorkoutConfig {
  exercises: number;
  setsPerExercise: number;
  exerciseTime: number;
  restBetweenSets: number;
  restBetweenExercises: number;
  supersetEnabled?: boolean;
  supersetPairs?: number;
  ordinaryExercises?: number;
  voiceRepCountEnabled?: boolean;
  repDuration?: number;
  name: string;
  useActivePlan: boolean;
  fromTrainingInProgress?: boolean;
  activePlanData?: any;
}

interface WorkoutHistoryEntry {
  id: string;
  type: string;
  date: string;
  duration: number;
  exercises?: number;
  totalSets?: number;
  quickSets?: number;
  slowSets?: number;
}

/** Iter299 — emit `<meta name="robots" content="noindex, follow">` when the
 *  current path is a duplicate alias of a real page, so Google keeps only the
 *  canonical URL in its index. */
function markAliasNoindex(aliases: string[]) {
  const p = appPathname().replace(/\/$/, '');
  let robots = document.querySelector('meta[name="robots"]');
  if (!robots) {
    robots = document.createElement('meta');
    robots.setAttribute('name', 'robots');
    document.head.appendChild(robots);
  }
  // Reset on the canonical path too — an in-session SPA navigation from the
  // alias would otherwise leave `noindex` on the real page.
  robots.setAttribute(
    'content',
    aliases.includes(p) ? 'noindex, follow' : 'index, follow, max-image-preview:large',
  );
}

function AppContent() {
  const [currentApp, setCurrentApp] = useState<string>(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('admin') === 'true') {
      return 'admin';
    }
    if (appPathname() === '/calculators') {
      return 'calculators';
    }
    if (
      appPathname() === '/muscular-potential-calculator' ||
      appPathname() === '/muscular-potential-calculator/'
    ) {
      return 'muscular-potential';
    }
    if (appPathname() === '/fatai' || appPathname() === '/fat-ai') {
      return 'fatai';
    }
    if (appPathname() === '/blog' || appPathname() === '/blog/') {
      return 'blog';
    }
    if (appPathname().startsWith('/blog/')) {
      return 'blog-post';
    }
    if (appPathname() === '/privacy' || appPathname() === '/privacy-policy') {
      // Iter299: `/privacy-policy` renders the same page as `/privacy` and
      // Google indexed BOTH despite the page's canonical. Mark the alias
      // `noindex, follow` so one canonical URL survives.
      markAliasNoindex(['/privacy-policy']);
      return 'privacy';
    }
    if (appPathname() === '/medical-disclaimer' || appPathname() === '/disclaimer') {
      markAliasNoindex(['/disclaimer']);
      return 'medical-disclaimer';
    }
    if (appPathname() === '/exercises' || appPathname() === '/exercises/') {
      return 'exercises';
    }
    // Per-muscle SEO landing pages — render the same index component but
    // seed it with the muscle filter. The component reads the path itself.
    if (appPathname().startsWith('/exercises/by-muscle/')) {
      return 'exercises';
    }
    if (appPathname().startsWith('/exercises/')) {
      return 'exercise-page';
    }
    if (appPathname() === '/trainers' || appPathname() === '/trainers/') {
      return 'trainers';
    }
    if (appPathname().startsWith('/trainers/')) {
      return 'trainer-page';
    }
    if (appPathname() === '/hall-of-fame' || appPathname() === '/hall-of-fame/') {
      return 'hall-of-fame';
    }
    // Iter304 — tokenised client-intake questionnaire link (`/intake?t=…`).
    // Login is required; the token is remembered across the auth round-trip.
    if (appPathname() === '/intake' || appPathname() === '/intake/') {
      return 'intake-form';
    }
    // Marketing subpages — full feature breakdown content, always public.
    // Listed BEFORE the legacy SEO routes block so they take precedence
    // even if a path overlaps (e.g. /nutrition).
    if (appPathname() === '/workout' || appPathname() === '/workout/') {
      return 'workout-subpage';
    }
    if (appPathname() === '/nutrition' || appPathname() === '/nutrition/') {
      return 'nutrition-subpage';
    }
    if (
      appPathname() === '/ai-planner-and-analyzer' ||
      appPathname() === '/ai-planner-and-analyzer/' ||
      // Also accept the mixed-case alias the user originally wrote
      appPathname() === '/AI-planner-and-analyzer' ||
      appPathname() === '/AI-planner-and-analyzer/'
    ) {
      return 'ai-planner-subpage';
    }
    // Iter252bt — Phantom URL fix (GSC "Duplicate, Google chose different
    // canonical"). Old inbound links and stale sitemap entries drove Google
    // to crawl `/pricing`, `/about`, `/features`, `/index.html`, `/terms`,
    // `/help`. None of those are real routes in the SPA — they all render
    // the landing page, which Google correctly flagged as duplicates of "/".
    //   Fix: for these paths only, emit `<meta name="robots" content="noindex,
    //   follow">` and rewrite the canonical to "/". Google will drop them
    //   from the index on the next crawl. All in-app internal links already
    //   use hash anchors (`/#pricing`, `/#features`).
    const PHANTOM_PATHS = new Set([
      '/pricing', '/pricing/',
      '/about', '/about/',
      '/features', '/features/',
      '/help', '/help/',
      '/terms', '/terms/',
      '/index.html',
      // iter299: `/gym-finder` is NOT a route — it renders the landing page,
      // and Google indexed it as a second copy of "/".
      '/gym-finder', '/gym-finder/',
    ]);
    if (PHANTOM_PATHS.has(appPathname())) {
      const setMeta = (sel: string, attr: string, val: string) => {
        let el = document.querySelector(sel);
        if (!el) {
          const isLink = sel.startsWith('link');
          el = document.createElement(isLink ? 'link' : 'meta');
          const m = sel.match(/\[([^=]+)="([^"]+)"\]/);
          if (m) el.setAttribute(m[1], m[2]);
          document.head.appendChild(el);
        }
        el.setAttribute(attr, val);
      };
      setMeta('meta[name="robots"]', 'content', 'noindex, follow');
      setMeta('link[rel="canonical"]', 'href', 'https://fitnessand.me/');
    }

    // Iter299 — alias paths that render a REAL page under a second URL.
    // The page's own `useSEO` already canonicalises to the primary path, yet
    // Google indexed both `/privacy` and `/privacy-policy`. Marking the alias
    // `noindex, follow` makes it drop out and leaves one canonical URL.

    // SEO routes — show landing page with route context for meta tags
    const seoRoutes = ['/nutrition', '/fridge', '/workouts', '/walking', '/statistics', '/photos'];
    if (seoRoutes.includes(appPathname())) {
      // Set meta tags for this route before rendering
      const routeMeta: Record<string, { title: string; desc: string }> = {
        '/nutrition': { title: 'AI Nutrition Tracker - Calorie Counter from Photo & Menu | Fitness&me', desc: 'Track calories, protein, carbs, fat and fiber automatically with AI photo scanning. Scan restaurant menus, search food database, set macro goals. Free nutrition tracking app.' },
        '/fridge': { title: 'AI Fridge Scanner - Scan Your Fridge for Recipe Ideas | Fitness&me', desc: 'Take a photo of your fridge and get instant recipe suggestions with full nutrition data. AI-powered meal planning from ingredients you already have.' },
        '/workouts': { title: 'AI Workout Plan Generator - Personalized Gym Programs | Fitness&me', desc: 'Get AI-generated workout plans tailored to your goals. Track sets, reps, and weights. Progressive overload tracking with deload weeks. Best gym tracker app.' },
        '/walking': { title: 'Walking Tracker & Step Counter - Track Steps, Distance, Calories | Fitness&me', desc: 'Track your daily walks with GPS, count steps, measure distance, and calculate calories burned. Background tracking with walking history and statistics.' },
        '/statistics': { title: 'Fitness Statistics & Progress Tracking - Body Measurements, AI Body Fat Analysis | Fitness&me', desc: 'Track your fitness progress with detailed statistics. Body weight trends, measurement history, AI body fat analysis from photos, workout records and personal bests.' },
        '/photos': { title: 'Progress Photos with AI Body Fat Analysis | Fitness&me', desc: 'Upload progress photos and get instant AI body fat percentage estimation. Track your physique transformation over time with before/after comparisons.' },
      };
      const meta = routeMeta[appPathname()];
      if (meta) {
        document.title = meta.title;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', meta.desc);
        // Per-route canonical (critical: without this Google treats these as duplicates of "/")
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.setAttribute('href', `https://fitnessand.me${appPathname()}`);
        // OG + Twitter url/title/description
        const setMeta = (sel: string, attr: string, val: string) => {
          const el = document.querySelector(sel);
          if (el) el.setAttribute(attr, val);
        };
        setMeta('meta[property="og:url"]', 'content', `https://fitnessand.me${appPathname()}`);
        setMeta('meta[property="og:title"]', 'content', meta.title);
        setMeta('meta[property="og:description"]', 'content', meta.desc);
        setMeta('meta[name="twitter:url"]', 'content', `https://fitnessand.me${appPathname()}`);
        setMeta('meta[name="twitter:title"]', 'content', meta.title);
        setMeta('meta[name="twitter:description"]', 'content', meta.desc);
      }
      // If user has token, go to notebook (they'll see the actual page)
      const existingToken = getToken();
      if (existingToken) return 'notebook';
      // Otherwise show landing with the route-specific meta tags (Googlebot sees these)
      return 'landing';
    }
    // If returning from Google auth, go straight to notebook
    const googleReturn = localStorage.getItem('google_auth_return_app');
    // Check both query params and hash for session_id (OAuth may use either)
    const hashParams = window.location.hash ? new URLSearchParams(window.location.hash.substring(1)) : null;
    const hasSessionId = urlParams.get('session_id') || hashParams?.get('session_id');
    if (googleReturn || hasSessionId) {
      return googleReturn || 'notebook';
    }
    // If user has a saved token, skip landing page to avoid flash of big text
    const existingToken = getToken();
    if (existingToken) {
      return 'notebook';
    }
    return 'landing';
  }); // 'landing', 'gym', 'walking', 'notebook', 'admin', 'calculators'
  const [screen, setScreen] = useState('setup');
  const [notebookInitialView, setNotebookInitialView] = useState<string | null>(null); // 'calendar', null, etc.
  const [blogSlug, setBlogSlug] = useState<string>(() => {
    const p = appPathname();
    return p.startsWith('/blog/') ? p.replace('/blog/', '').replace(/\/$/, '') : '';
  });
  const [exerciseSlug, setExerciseSlug] = useState<string>(() => {
    const p = appPathname();
    return p.startsWith('/exercises/') ? p.replace('/exercises/', '').replace(/\/$/, '') : '';
  });
  const [trainerSlug, setTrainerSlug] = useState<string>(() => {
    const p = appPathname();
    return p.startsWith('/trainers/') ? p.replace('/trainers/', '').replace(/\/$/, '') : '';
  });
  const [workoutConfig, setWorkoutConfig] = useState<WorkoutConfig | null>(null);
  // Initialize walkingConfig from localStorage if a session exists
  const [walkingConfig, setWalkingConfig] = useState<WalkingConfig | null>(() => {
    try {
      const savedSession = localStorage.getItem('walking_active_session');
      if (savedSession) {
        const session = JSON.parse(savedSession);
        const today = new Date().toISOString().split('T')[0];
        const sessionAge = Date.now() - (session.savedAt || 0);
        const twelveHours = 12 * 60 * 60 * 1000;
        if (session.date === today && sessionAge < twelveHours) {
          // Create a minimal config from the saved session
          const parts = (session.configId || '').split('-');
          return {
            quickWalkingTime: parseInt(parts[0]) || 300,
            slowWalkingTime: parseInt(parts[1]) || 180,
            sets: 1 // Default
          };
        }
      }
    } catch (e) {
      console.error('[App] Error restoring walking config:', e);
    }
    return null;
  });
  const [soundEnabled, setSoundEnabled] = useState(true);
  const { user, refreshUser, isAuthenticated, loading: authLoading, pending2FA, logout } = useAuth();
  // Onboarding gate state: null = not yet fetched, true/false = fetched.
  // null is treated as "loading", so we show a spinner instead of the app to
  // avoid flashing the dashboard before the wizard.
  const [onboardingDone, setOnboardingDone] = useState<boolean | null>(null);
  useEffect(() => {
    if (!isAuthenticated || authLoading || pending2FA) { return; }
    // Trainers skip the consumer onboarding entirely.
    if (user && (user as any).account_type === 'trainer') { setOnboardingDone(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding/status`, {
          headers: { Authorization: `Bearer ${getToken() || ''}` },
        });
        const data = await r.json();
        if (!cancelled) setOnboardingDone(Boolean(data?.completed));
      } catch {
        if (!cancelled) setOnboardingDone(true); // fail open — don't lock the user out
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, authLoading, pending2FA, user]);
  
  // Timer persistence state - keeps timer running when navigating away
  const [activeTimerState, setActiveTimerState] = useState(null); // { timeLeft, phase, currentExercise, currentSet, config }
  const [timerWasActive, setTimerWasActive] = useState(false);
  
  // Walking session persistence - keeps walking active when navigating away
  // Initialize from localStorage to show floating button after page refresh
  const [walkingSessionActive, setWalkingSessionActive] = useState(() => {
    try {
      const savedSession = localStorage.getItem('walking_active_session');
      if (savedSession) {
        const session = JSON.parse(savedSession);
        // Check if session is from today and not too old (within 12 hours)
        const today = new Date().toISOString().split('T')[0];
        const sessionAge = Date.now() - (session.savedAt || 0);
        const twelveHours = 12 * 60 * 60 * 1000;
        if (session.date === today && sessionAge < twelveHours) {
          console.log('[App] Restored walking session active state from localStorage');
          return true;
        }
      }
    } catch (e) {
      console.error('[App] Error checking walking session:', e);
    }
    return false;
  });
  
  // App Version - single source of truth in /src/constants/appVersion.ts
  
  // Detect iOS devices
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  
  // Version check disabled to prevent refresh loops
  // Updates are now handled manually by the user refreshing the page
  
  // Handle Polar.sh payment redirect
  useEffect(() => {
    if (authLoading) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const polarSuccess = urlParams.get('polar_checkout_success');
    
    if (polarSuccess === 'true' && isAuthenticated) {
      const activateSubscription = async () => {
        try {
          await refreshUser();
          toast.success('Payment successful! Subscription activated.');
        } catch (err) {
          console.error('Subscription activation failed:', err);
        }
        window.history.replaceState({}, document.title, appPathname());
      };
      activateSubscription();
    }
  }, [isAuthenticated, authLoading, refreshUser]);

  // Auto-navigate to notebook after Google auth redirect, or back to landing if auth fails
  useEffect(() => {
    if (!authLoading && !pending2FA) {
      const returnApp = localStorage.getItem('google_auth_return_app');
      if (returnApp) {
        localStorage.removeItem('google_auth_return_app');
        if (isAuthenticated) {
          setCurrentApp(returnApp);
        } else {
          // Auth failed — go back to landing
          setCurrentApp('landing');
        }
      }
    }
  }, [isAuthenticated, authLoading, pending2FA]);

  // Single tracking point for every PUBLIC page. Replaces the per-component
  // `usePageView` calls, which covered only 4 of ~20 routes — exercise,
  // trainer, blog, hall-of-fame and the marketing subpages recorded nothing
  // at all, so there was no way to see whether SEO traffic arrived.
  // Logged-in app screens are intentionally not tracked.
  usePublicPageView(currentApp);

  // Failsafe: if user is authenticated but stuck on landing, navigate to notebook
  useEffect(() => {
    if (isAuthenticated && !authLoading && !pending2FA && currentApp === 'landing') {
      // Iter304 — a client who opened an intake link while logged out is sent
      // back to that form instead of the dashboard once they sign in.
      let pendingIntake = '';
      try { pendingIntake = sessionStorage.getItem('pendingIntakeToken') || ''; } catch { /* noop */ }
      if (pendingIntake) {
        try { sessionStorage.removeItem('pendingIntakeToken'); } catch { /* noop */ }
        window.history.pushState({}, '', `/intake?t=${pendingIntake}`);
        setCurrentApp('intake-form');
        return;
      }
      console.log('[App] Authenticated but on landing — navigating to notebook');
      setCurrentApp('notebook');
    }
  }, [isAuthenticated, authLoading, pending2FA, currentApp]);

  // Clarity records the PUBLIC marketing surface only (heatmaps + rage clicks
  // there drive conversion insight). It is never loaded for a session that
  // starts logged-in, but a visitor can sign in without a page reload — pause
  // capture on that transition and resume it if they land back on marketing.
  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated && currentApp !== 'landing') clarityPause();
    else if (!isAuthenticated) clarityResume();
  }, [isAuthenticated, authLoading, currentApp]);


  // Listen to phone hardware back / browser back-button → resync currentApp
  // from window.location. Without this, pressing Android back on /calculators
  // or /fatai pops the URL but leaves the React state stuck on the page.
  useEffect(() => {
    const onPopState = () => {
      const path = appPathname();
      let next: string;
      if (path === '/calculators') next = 'calculators';
      else if (path === '/muscular-potential-calculator' || path === '/muscular-potential-calculator/') next = 'muscular-potential';
      else if (path === '/fatai' || path === '/fat-ai') next = 'fatai';
      else if (path === '/blog' || path === '/blog/') next = 'blog';
      else if (path.startsWith('/blog/')) next = 'blog-post';
      else if (path === '/privacy' || path === '/privacy-policy') next = 'privacy';
      else if (path === '/medical-disclaimer' || path === '/disclaimer') next = 'medical-disclaimer';
      else if (path === '/exercises' || path === '/exercises/') next = 'exercises';
      else if (path.startsWith('/exercises/by-muscle/')) next = 'exercises';
      else if (path.startsWith('/exercises/')) next = 'exercise-page';
      else if (path === '/trainers' || path === '/trainers/') next = 'trainers';
      else if (path.startsWith('/trainers/')) next = 'trainer-page';
      else if (path === '/hall-of-fame' || path === '/hall-of-fame/') next = 'hall-of-fame';
      else if (path === '/intake' || path === '/intake/') next = 'intake-form';
      else if (path === '/workout' || path === '/workout/') next = 'workout-subpage';
      else if (path === '/nutrition' || path === '/nutrition/') next = 'nutrition-subpage';
      else if (
        path === '/ai-planner-and-analyzer' || path === '/ai-planner-and-analyzer/' ||
        path === '/AI-planner-and-analyzer' || path === '/AI-planner-and-analyzer/'
      ) next = 'ai-planner-subpage';
      else next = isAuthenticated ? 'notebook' : 'landing';
      setCurrentApp(next);
      window.scrollTo(0, 0);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isAuthenticated]);

  
  // AGGRESSIVE CLEANUP: Remove all old training data on app start
  // Scroll to top on page change
  useEffect(() => { window.scrollTo(0, 0); }, [currentApp]);

  // Tag <html data-route> based on the active view. The static mobile hero
  // backdrop in public/index.html is scoped via CSS to only show on
  // `html[data-route="landing"]`, so this effect ensures it disappears
  // the moment the user leaves the marketing surface (e.g. clicks Log In
  // → dashboard) and reappears when they navigate back.
  //
  // Also uses this same edge to lazy-load the ~200 KB Material Symbols icon
  // font — it's used across the authenticated app (Photos, Statistics,
  // Wearables, Calculators, Trainer Dashboard, etc.) but NEVER on landing,
  // so paying for it on the marketing surface was pure waste (iter184 PSI).
  useEffect(() => {
    try {
      document.documentElement.setAttribute(
        'data-route',
        currentApp === 'landing' ? 'landing' : 'app'
      );
    } catch { /* SSR / non-DOM env */ }

    if (currentApp !== 'landing') {
      const id = 'material-symbols-outlined-css';
      if (typeof document !== 'undefined' && !document.getElementById(id)) {
        const l = document.createElement('link');
        l.id = id;
        l.rel = 'stylesheet';
        l.href = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap';
        document.head.appendChild(l);
      }
    }
  }, [currentApp]);


  useEffect(() => {
    const cleanupOldData = () => {
      const cutoffDate = new Date('2025-01-13T00:00:00Z').getTime();
      
      // List of all possible storage keys for training data
      const keysToClean = [
        'gym_notebook_training_history',
        'fitpro_training_history',
        'training_history',
        'gym_notebook_active_plan',
        'gym_notebook_exercise_history',
        'gym_notebook_favorites'
      ];
      
      let totalRemoved = 0;
      
      keysToClean.forEach(key => {
        const saved = localStorage.getItem(key);
        if (saved) {
          try {
            const data = JSON.parse(saved);
            if (Array.isArray(data)) {
              const cleaned = data.filter(entry => {
                const dateStr = entry.date || entry.completedAt || '';
                if (!dateStr) return true; // Keep entries without dates
                
                // Specifically remove 11.01 and 12.01 entries (both 2025 and 2026)
                if (dateStr.includes('2025-01-11') || dateStr.includes('2025-01-12') ||
                    dateStr.includes('2026-01-11') || dateStr.includes('2026-01-12')) {
                  console.log(`[Cleanup] Removing old entry: ${dateStr}`);
                  return false;
                }
                
                const entryDate = new Date(dateStr).getTime();
                return !isNaN(entryDate) && entryDate >= cutoffDate;
              });
              
              if (cleaned.length !== data.length) {
                const removed = data.length - cleaned.length;
                totalRemoved += removed;
                console.log(`[Cleanup] Removed ${removed} old entries from ${key}`);
                localStorage.setItem(key, JSON.stringify(cleaned));
              }
            }
          } catch (e) {
            console.log(`[Cleanup] Clearing corrupted data from ${key}`);
            localStorage.removeItem(key);
          }
        }
      });
      
      // Also clean exercise history object (not array)
      const exerciseHistoryKey = 'gym_notebook_exercise_history';
      const exerciseHistory = localStorage.getItem(exerciseHistoryKey);
      if (exerciseHistory) {
        try {
          const data = JSON.parse(exerciseHistory);
          if (typeof data === 'object' && !Array.isArray(data)) {
            const cleaned = {};
            Object.keys(data).forEach(exerciseName => {
              const entries = data[exerciseName];
              if (Array.isArray(entries)) {
                cleaned[exerciseName] = entries.filter(entry => {
                  const dateStr = entry.date || '';
                  if (dateStr.includes('2025-01-11') || dateStr.includes('2025-01-12') ||
                      dateStr.includes('2026-01-11') || dateStr.includes('2026-01-12')) {
                    return false;
                  }
                  const entryDate = new Date(dateStr).getTime();
                  return !isNaN(entryDate) && entryDate >= cutoffDate;
                });
              }
            });
            localStorage.setItem(exerciseHistoryKey, JSON.stringify(cleaned));
          }
        } catch (e) {
          // Ignore
        }
      }
      
      if (totalRemoved > 0) {
        console.log(`[Cleanup] Total removed: ${totalRemoved} old entries`);
      }
    };
    
    cleanupOldData();
  }, []);
  
  // Use lazy initialization for localStorage values
  const [workoutHistory, setWorkoutHistory] = useState(() => {
    const saved = localStorage.getItem('workoutHistory');
    return saved ? JSON.parse(saved) : [];
  });
  const [bgTheme, setBgTheme] = useState(() => {
    return localStorage.getItem('bgTheme') || 'steel-gray';
  });

  // Load walking data from backend on mount when authenticated
  useEffect(() => {
    const loadWalkingDataFromBackend = async () => {
      const token = getToken();
      if (!token) return;
      
      try {
        // Load walking sessions
        const sessionsResponse = await fetch(`${API_URL}/api/data/walking-sessions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (sessionsResponse.ok) {
          const sessionsData = await sessionsResponse.json();
          if (sessionsData.sessions && sessionsData.sessions.length > 0) {
            // Merge with localStorage (backend takes priority)
            const localSessions = JSON.parse(localStorage.getItem('walking_session_history') || '[]');
            const mergedSessions = [...sessionsData.sessions];
            // Add local sessions that aren't in backend (by id)
            const backendIds = new Set(sessionsData.sessions.map(s => s.id));
            localSessions.forEach(s => {
              if (!backendIds.has(s.id)) {
                mergedSessions.push(s);
              }
            });
            localStorage.setItem('walking_session_history', JSON.stringify(mergedSessions));
            console.log('Walking sessions loaded from backend:', mergedSessions.length);
          }
        }
        
        // Load walking daily data
        const dailyResponse = await fetch(`${API_URL}/api/data/walking-daily`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (dailyResponse.ok) {
          const dailyData = await dailyResponse.json();
          if (dailyData.daily_data && Object.keys(dailyData.daily_data).length > 0) {
            // Merge with localStorage (walking_daily_data)
            const localDaily = JSON.parse(localStorage.getItem('walking_daily_data') || '{}');
            const mergedDaily = { ...localDaily, ...dailyData.daily_data };
            localStorage.setItem('walking_daily_data', JSON.stringify(mergedDaily));
            
            // Also sync walking_daily_steps (flat steps map) so WalkingSetup/Dashboard show correct values
            const existingSteps = JSON.parse(localStorage.getItem('walking_daily_steps') || '{}');
            let stepsUpdated = false;
            Object.entries(dailyData.daily_data).forEach(([date, data]: [string, any]) => {
              const backendSteps = data?.steps || 0;
              if (backendSteps > (existingSteps[date] || 0)) {
                existingSteps[date] = backendSteps;
                stepsUpdated = true;
              }
            });
            if (stepsUpdated) {
              localStorage.setItem('walking_daily_steps', JSON.stringify(existingSteps));
            }
            
            console.log('Walking daily data loaded from backend');
          }
        }
        
        // Load step goal settings from backend (for dashboard step ring)
        try {
          const goalResponse = await fetch(`${API_URL}/api/data/walking-step-goal`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (goalResponse.ok) {
            const goalData = await goalResponse.json();
            if (goalData?.settings && Object.keys(goalData.settings).length > 0) {
              localStorage.setItem('walking_step_goal_settings', JSON.stringify(goalData.settings));
            }
          }
        } catch { /* step goal fetch failed, keep local settings */ }
      } catch (error) {
        console.error('Error loading walking data from backend:', error);
      }
    };
    
    if (isAuthenticated && !authLoading) {
      loadWalkingDataFromBackend();
    }
  }, [isAuthenticated, authLoading]);

  const handleThemeChange = (themeId) => {
    setBgTheme(themeId);
    localStorage.setItem('bgTheme', themeId);
  };

  const getBackgroundClass = () => {
    const themes = {
      // Modern & Minimal
      'midnight-slate': 'bg-[hsl(220,20%,10%)]',
      'pure-dark': 'bg-[hsl(0,0%,8%)]',
      // Bold & Energetic
      'fire-red': 'bg-[hsl(0,60%,12%)]',
      'electric-blue': 'bg-[hsl(210,70%,12%)]',
      'neon-green': 'bg-[hsl(150,60%,10%)]'
    };
    return themes[bgTheme] || themes['midnight-slate'];
  };

  const handleSelectApp = useCallback((app: string) => {
    setCurrentApp(app);
    setScreen('setup');
  }, []);

  const handleBackToLanding = () => {
    setCurrentApp('landing');
    setScreen('setup');
    setWorkoutConfig(null);
    // Don't clear walkingConfig if walking session is still active
    if (!walkingSessionActive) {
      setWalkingConfig(null);
    }
  };

  const handleStartWorkout = (config) => {
    setWorkoutConfig(config);
    setScreen('timer');
  };

  const handleStartWalkingWorkout = (config) => {
    setWalkingConfig(config);
    setWalkingSessionActive(true);
    setScreen('walking-timer');
  };

  const handleWorkoutComplete = (summary) => {
    const newHistory = [summary, ...workoutHistory].slice(0, 20);
    setWorkoutHistory(newHistory);
    localStorage.setItem('workoutHistory', JSON.stringify(newHistory));
    setScreen('setup');
  };

  const handleWalkingComplete = async (summary) => {
    console.log('[Walking] handleWalkingComplete called with:', summary);
    
    // Clear walking session active state
    setWalkingSessionActive(false);
    
    // Save walking workout to history
    const walkingSummary = {
      id: Date.now(),
      date: new Date().toISOString(),
      type: 'walking',
      duration: summary.totalTime,
      quickSets: summary.quickSets,
      slowSets: summary.slowSets,
      steps: summary.steps || 0,
      totalDailySteps: summary.totalDailySteps || 0,
      calories: summary.calories || 0,
      bodyWeight: summary.bodyWeight || 70,
      completed: !summary.incomplete // Use the incomplete flag from Save & Exit
    };
    
    try {
      // Save to workout history
      const newHistory = [walkingSummary, ...workoutHistory].slice(0, 20);
      setWorkoutHistory(newHistory);
      localStorage.setItem('workoutHistory', JSON.stringify(newHistory));
      console.log('[Walking] Saved to workoutHistory');
      
      // Also save to walking-specific storage for NutritionTracker
      const todayDate = new Date().toISOString().split('T')[0];
      const walkingSessions = JSON.parse(localStorage.getItem('walking_session_history') || '[]');
      walkingSessions.push(walkingSummary);
      localStorage.setItem('walking_session_history', JSON.stringify(walkingSessions));
      console.log('[Walking] Saved to walking_session_history');
      
      // Update walking daily data (aggregated totals)
      const walkingDaily = JSON.parse(localStorage.getItem('walking_daily_data') || '{}');
      const existingDaily = walkingDaily[todayDate] || { steps: 0, distance: 0, calories: 0 };
      walkingDaily[todayDate] = {
        steps: (existingDaily.steps || 0) + (summary.steps || 0),
        distance: (existingDaily.distance || 0) + (summary.distance || 0),
        calories: (existingDaily.calories || 0) + (summary.calories || 0)
      };
      localStorage.setItem('walking_daily_data', JSON.stringify(walkingDaily));
      
      console.log('[Walking] Saved to walking_daily_data:', walkingDaily[todayDate]);
      
      // Show success toast
      if (summary.steps > 0 || summary.calories > 0) {
        toast.success(`Saved: ${summary.steps || 0} steps, ${summary.calories || 0} kcal`);
      }
      
      // Sync to backend if authenticated (non-blocking)
      const token = getToken();
      if (token) {
        // iter354 — queued when offline so a walk logged with no signal is
        // uploaded as soon as the connection is back.
        sendOrQueue(`${API_URL}/api/data/walking-sessions`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ sessions: walkingSessions })
        }, { label: 'Walking sessions', dedupeKey: 'PUT:/api/data/walking-sessions' }).then((r) => {
          if (r) window.dispatchEvent(new CustomEvent('dataChanged'));
        });

        sendOrQueue(`${API_URL}/api/data/walking-daily/${todayDate}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(walkingDaily[todayDate])
        }, { label: `Walking ${todayDate}`, dedupeKey: `PUT:/api/data/walking-daily/${todayDate}` }).then((r) => {
          if (r) window.dispatchEvent(new CustomEvent('dataChanged'));
        });
      }
    } catch (error) {
      console.error('[Walking] Error saving walking data:', error);
      toast.error('Failed to save walking data');
    }
    
    handleBackToLanding();
  };

  const handleGoHome = () => {
    setScreen('setup');
    setWorkoutConfig(null);
  };

  // Admin Panel - accessible via ?admin=true
  if (currentApp === 'admin') {
    return (
      <>
        <AdminPanel />
      </>
    );
  }

  // Calculators Page
  if (currentApp === 'calculators') {
    return (
      <>
        <CalculatorsPage onBack={() => {
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
        }} />
      </>
    );
  }

  // Muscular Potential Calculator (Casey Butt) — public SEO page
  if (currentApp === 'muscular-potential') {
    return (
      <MuscularPotentialPage
        onBack={() => {
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
        }}
        onOpenCalculators={() => {
          setCurrentApp('calculators');
          window.history.pushState({}, '', '/calculators.html');
          window.scrollTo(0, 0);
        }}
      />
    );
  }

  // Fat%AI Public Page (free, no-auth)
  if (currentApp === 'fatai') {
    return (
      <>
        <FatAIPage onBack={() => {
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
        }} />
      </>
    );
  }

  // Blog Index Page
  if (currentApp === 'blog') {
    return (
      <BlogIndex
        onBack={() => {
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
        }}
        onOpenPost={(slug) => {
          setBlogSlug(slug);
          setCurrentApp('blog-post');
          window.history.pushState({}, '', `/blog/${slug}.html`);
          window.scrollTo(0, 0);
        }}
      />
    );
  }

  // Blog Post Page
  if (currentApp === 'blog-post') {
    return (
      <BlogPost
        slug={blogSlug}
        onBack={() => {
          setCurrentApp('blog');
          setBlogSlug('');
          window.history.pushState({}, '', '/blog.html');
          window.scrollTo(0, 0);
        }}
        onOpenPost={(slug) => {
          setBlogSlug(slug);
          window.history.pushState({}, '', `/blog/${slug}.html`);
          window.scrollTo(0, 0);
        }}
        onOpenFatAI={() => {
          setCurrentApp('fatai');
          window.history.pushState({}, '', '/fatai.html');
          window.scrollTo(0, 0);
        }}
        onOpenCalculators={() => {
          setCurrentApp('calculators');
          window.history.pushState({}, '', '/calculators.html');
          window.scrollTo(0, 0);
        }}
      />
    );
  }

  // Privacy Policy Page (desktop/tablet; mobile shows a modal elsewhere)
  if (currentApp === 'privacy') {
    return (
      <PrivacyPolicyPage
        onBack={() => {
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
      />
    );
  }

  // Medical Disclaimer Page
  if (currentApp === 'medical-disclaimer') {
    return (
      <MedicalDisclaimerPage
        onBack={() => {
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
      />
    );
  }

  // Public exercise library (SEO landing pages)
  if (currentApp === 'exercises') {
    return (
      <ExercisesIndex
        onBack={() => {
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
        onOpenExercise={(slug) => {
          setExerciseSlug(slug);
          setCurrentApp('exercise-page');
          window.history.pushState({}, '', `/exercises/${slug}.html`);
          window.scrollTo(0, 0);
        }}
        onOpenAuth={(_mode) => {
          // Auth modal is owned by LandingPage — bounce there.
          // The mode hint is preserved via sessionStorage so the landing
          // page can auto-open the right form (signup/login).
          try { sessionStorage.setItem('pendingAuthMode', _mode); } catch { /* noop */ }
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
      />
    );
  }

  if (currentApp === 'exercise-page') {
    return (
      <ExercisePage
        slug={exerciseSlug}
        onBack={() => {
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
        onBackToList={() => {
          setExerciseSlug('');
          setCurrentApp('exercises');
          window.history.pushState({}, '', '/exercises.html');
          window.scrollTo(0, 0);
        }}
        onOpenAuth={(_mode) => {
          try { sessionStorage.setItem('pendingAuthMode', _mode); } catch { /* noop */ }
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
      />
    );
  }

  if (currentApp === 'trainers') {
    return (
      <TrainersIndex
        onBack={() => {
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
        onOpenTrainer={(slug) => {
          setTrainerSlug(slug);
          setCurrentApp('trainer-page');
          window.history.pushState({}, '', `/trainers/${slug}.html`);
          window.scrollTo(0, 0);
        }}
        onOpenAuth={(_mode) => {
          try { sessionStorage.setItem('pendingAuthMode', _mode); } catch { /* noop */ }
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
        onOpenDashboard={() => {
          setCurrentApp('notebook');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
        isAuthenticated={isAuthenticated}
      />
    );
  }

  if (currentApp === 'trainer-page') {
    return (
      <TrainerProfilePage
        slug={trainerSlug}
        onBack={() => {
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
        onBackToList={() => {
          setTrainerSlug('');
          setCurrentApp('trainers');
          window.history.pushState({}, '', '/trainers.html');
          window.scrollTo(0, 0);
        }}
        onOpenAuth={(_mode) => {
          try { sessionStorage.setItem('pendingAuthMode', _mode); } catch { /* noop */ }
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
      />
    );
  }

  if (currentApp === 'hall-of-fame') {    return (
      <HallOfFamePage
        onBack={() => {
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
        onOpenTrainer={(slug) => {
          setTrainerSlug(slug);
          setCurrentApp('trainer-page');
          window.history.pushState({}, '', `/trainers/${slug}.html`);
          window.scrollTo(0, 0);
        }}
        onOpenAuth={(_mode) => {
          try { sessionStorage.setItem('pendingAuthMode', _mode); } catch { /* noop */ }
          setCurrentApp('landing');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
        onOpenDashboard={() => {
          setCurrentApp('notebook');
          window.history.pushState({}, '', '/');
          window.scrollTo(0, 0);
        }}
        isAuthenticated={isAuthenticated}
      />
    );
  }

  // Iter304 — client intake questionnaire (`/intake?t=<token>`). Login is
  // required: the token is stashed so the client lands back here after auth.
  if (currentApp === 'intake-form') {
    const token = new URLSearchParams(window.location.search).get('t') || '';
    const goToApp = () => {
      setCurrentApp(isAuthenticated ? 'notebook' : 'landing');
      window.history.pushState({}, '', '/');
      window.scrollTo(0, 0);
    };
    if (authLoading) {
      return <BootDots />;
    }
    if (!isAuthenticated) {
      return (
        <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center px-6">
          <div className="max-w-md text-center" data-testid="intake-login-required">
            <h1 className="font-headline text-3xl font-bold text-[#f1f3fc] mb-2">Your trainer sent you a form</h1>
            <p className="text-[#a8abb3] text-sm mb-8">
              Log in to your Fitness&amp;me account to fill it in — your answers are saved to your account.
            </p>
            <button
              onClick={() => {
                try {
                  sessionStorage.setItem('pendingIntakeToken', token);
                  sessionStorage.setItem('pendingAuthMode', 'login');
                } catch { /* noop */ }
                setCurrentApp('landing');
                window.history.pushState({}, '', '/');
                window.scrollTo(0, 0);
              }}
              data-testid="intake-login-btn"
              className="px-7 py-3 rounded-full bg-[#cafd00] text-[#0a0e14] font-bold text-sm active:scale-95 transition-all"
            >
              Log in to continue
            </button>
          </div>
        </div>
      );
    }
    return (
      <Suspense fallback={<BootDots />}>
        <IntakeFormPage token={token} onExit={goToApp} />
      </Suspense>
    );
  }

  // Marketing subpages — public feature breakdowns. Always rendered
  // regardless of auth (these are top-of-funnel SEO landings).
  if (currentApp === 'workout-subpage') {
    return <WorkoutSubpage />;
  }
  if (currentApp === 'nutrition-subpage') {
    return <NutritionSubpage />;
  }
  if (currentApp === 'ai-planner-subpage') {
    return <AiPlannerSubpage />;
  }

  // Landing Page. Historically we blocked rendering until `authLoading` was
  // false to avoid a flash of the big landing hero on refresh for signed-in
  // users. That gate cost ~2-3s of LCP on Slow-4G though — the H1 became
  // the LCP element and its Element Render Delay ballooned to 3.5s because
  // it couldn't paint until `/api/auth/me` resolved.
  //
  // Compromise: only hold on the loader when a token is present in sync
  // storage (returning-user path — flash prevention still applies). For
  // anonymous visitors (Lighthouse tests, first-time users, most SEO
  // traffic) there's no token, so we skip the wait and render landing
  // immediately. This unblocks LCP by ~2-3s on mobile without regressing
  // the signed-in refresh UX.
  if (currentApp === 'landing') {
    if (authLoading && getToken()) {
      return <BootDots />;
    }
    return (
      <>
        <LandingPage 
          onSelectApp={handleSelectApp} 
          onOpenCalculators={() => {
            setCurrentApp('calculators');
            window.history.pushState({}, '', '/calculators.html');
          }}
          onOpenFatAI={() => {
            setCurrentApp('fatai');
            window.history.pushState({}, '', '/fatai.html');
          }}
        />
        
        {/* Floating Walking Button - Shows when walking session is active */}
        {walkingSessionActive && walkingConfig && (
          <button
            onClick={() => {
              setCurrentApp('walking');
              setScreen('walking-timer');
            }}
            className="fixed bottom-24 right-4 z-50 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-3 rounded-full shadow-lg shadow-cyan-500/30 flex items-center gap-2 animate-pulse hover:animate-none hover:scale-105 transition-transform"
            data-testid="walking-active-indicator"
          >
            <Footprints className="w-5 h-5" />
            <span className="font-semibold">Walking Active</span>
          </button>
        )}
        
      </>
    );
  }

  // Block notebook render until auth is confirmed (prevents dashboard flash before 2FA modal)
  if (currentApp === 'notebook' && !isAuthenticated) {
    if (authLoading || pending2FA) {
      // Show the boot dots while auth is being checked or 2FA is in progress
      return <BootDots />;
    }
    // Auth finished but not authenticated — redirect to landing immediately
    // (useEffect below will also fire, but this prevents a blank screen flash)
    setCurrentApp('landing');
    return null;
  }

  // First-signup onboarding gate. Only mount the wizard once we have an
  // authenticated, non-trainer user whose `onboarding_completed` flag is
  // explicitly false. While we're still resolving the flag we render a tiny
  // spinner over the dashboard (avoids a flash of the dashboard followed by
  // the onboarding overlay).
  if (
    isAuthenticated && !authLoading && !pending2FA &&
    user && (user as any).account_type !== 'trainer' &&
    currentApp === 'notebook'
  ) {
    if (onboardingDone === null) {
      return <BootDots />;
    }
    if (onboardingDone === false) {
      return (
        <OnboardingFlow
          onComplete={async () => {
            setOnboardingDone(true);
            try { await refreshUser(); } catch { /* non-fatal */ }
          }}
        />
      );
    }
  }

  // Gym Notebook App
  if (currentApp === 'notebook') {
    // Trainers get a different dashboard — they view their clients, not their own notebook.
    if (user && (user as any).account_type === 'trainer') {
      return (
        <TrainerClientDashboard
          trainerName={user.name || 'Trainer'}
          onLogout={async () => {
            try { await logout(); } catch { /* swallow */ }
            setCurrentApp('landing');
            window.history.pushState({}, '', '/');
          }}
        />
      );
    }
    const handleGoToGymTimer = () => {
      setCurrentApp('gym');
      setScreen('setup');
    };
    
    const handleGoToWalking = (config?) => {
      if (config) {
        handleStartWalkingWorkout(config);
        setCurrentApp('walking');
      } else {
        setCurrentApp('walking');
        setScreen('setup');
      }
    };
    
    return (
      <>
        <GymNotebook 
          onBack={handleBackToLanding} 
          onGoToGymTimer={handleGoToGymTimer}
          onGoToWalking={handleGoToWalking}
          initialView={notebookInitialView}
          onViewShown={() => setNotebookInitialView(null)}
        />
        
        {/* Floating Timer Button - Shows when timer is active but user navigated away */}
        {timerWasActive && currentApp === 'notebook' && screen !== 'timer' && (
          <button
            onClick={() => {
              setCurrentApp('gym');
              setScreen('timer');
            }}
            className="fixed bottom-24 right-4 z-50 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-3 rounded-full shadow-lg shadow-green-500/30 flex items-center gap-2 animate-pulse hover:animate-none hover:scale-105 transition-transform"
          >
            <div className="w-3 h-3 bg-white rounded-full animate-ping" />
            <span className="font-semibold">Timer Active</span>
          </button>
        )}
        
        {/* Floating Walking Button - Shows when walking session is active */}
        {walkingSessionActive && walkingConfig && (
          <button
            onClick={() => {
              setCurrentApp('walking');
              setScreen('walking-timer');
            }}
            className="fixed bottom-24 right-4 z-50 bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-3 rounded-full shadow-lg shadow-cyan-500/30 flex items-center gap-2 animate-pulse hover:animate-none hover:scale-105 transition-transform"
            data-testid="walking-active-indicator-notebook"
          >
            <Footprints className="w-5 h-5" />
            <span className="font-semibold">Walking Active</span>
          </button>
        )}
        
      </>
    );
  }

  // Walking Timer App
  if (currentApp === 'walking') {
    return (
      <div className={`min-h-screen text-foreground ${getBackgroundClass()}`}>
        <header className={`sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border ${screen === 'walking-timer' ? 'hidden' : ''}`}>
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl blur opacity-40"></div>
                <div className="relative w-full h-full rounded-xl bg-[#0a0e14] border border-cyan-500/30 flex items-center justify-center">
                  <Footprints className="w-6 h-6 text-cyan-400" />
                </div>
              </div>
              <h1 className="text-xl font-bold tracking-tight text-cyan-400">Walking Timer</h1>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="rounded-full"
              >
                {soundEnabled ? (
                  <Volume2 className="h-5 w-5 text-green-400" />
                ) : (
                  <VolumeX className="h-5 w-5 text-[#a8abb3]" />
                )}
              </Button>
              
              {screen === 'setup' && (
                <>
                  {/* Calendar Button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setNotebookInitialView('calendar');
                      setCurrentApp('notebook');
                    }}
                    className="rounded-full"
                    title="Calendar"
                  >
                    <CalendarDays className="h-5 w-5 text-cyan-400" />
                  </Button>
                  
                  {/* Background Theme Dialog */}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-full">
                        <Palette className="h-5 w-5 text-purple-400" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#1b2028] border-slate-700 max-w-md max-h-[85vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Background Theme</DialogTitle>
                      </DialogHeader>
                      <BackgroundTheme currentTheme={bgTheme} onThemeChange={handleThemeChange} />
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-6">
          {screen === 'setup' && (
            <WalkingSetup 
              onStartWorkout={handleStartWalkingWorkout}
              onBack={() => setCurrentApp('notebook')}
            />
          )}
          {/* Walking Timer - Keep mounted to preserve GPS tracking */}
          {walkingConfig && (
            <div style={{ display: screen === 'walking-timer' ? 'block' : 'none' }}>
              <WalkingTimer
                config={walkingConfig}
                onComplete={handleWalkingComplete}
                onBack={() => {
                  // Navigate back to notebook but keep walking session active
                  setCurrentApp('notebook');
                }}
              />
            </div>
          )}
        </main>

      </div>
    );
  }

  // Gym Timer App
  return (
    <div className={`min-h-screen text-foreground ${getBackgroundClass()}`}>
      <header className="sticky top-0 z-50 backdrop-blur-lg bg-background/80 border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBackToLanding}
              className="rounded-full"
              data-testid="gym-back-to-landing-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-white p-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_timerduo/artifacts/dqh6zpye_Timer.png"
                alt="Gym Timer"
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="text-xl font-bold tracking-tight text-orange-400">Gym Timer</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="rounded-full"
            >
              {soundEnabled ? (
                <Volume2 className="h-5 w-5" />
              ) : (
                <VolumeX className="h-5 w-5" />
              )}
            </Button>
            {screen !== 'setup' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleGoHome}
                className="rounded-full"
              >
                <Home className="h-5 w-5" />
              </Button>
            )}
            {screen === 'setup' && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setScreen('history')}
                  className="rounded-full"
                >
                  <History className="h-5 w-5" />
                </Button>
                
                {/* Background Theme Dialog */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full">
                      <Palette className="h-5 w-5" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#1b2028] border-slate-700 max-w-md max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Background Theme</DialogTitle>
                    </DialogHeader>
                    <BackgroundTheme currentTheme={bgTheme} onThemeChange={handleThemeChange} />
                  </DialogContent>
                </Dialog>
                
                {/* Voice Recordings Dialog */}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full">
                      <Mic className="h-5 w-5" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#1b2028] border-slate-700 max-w-md max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Voice Recordings</DialogTitle>
                    </DialogHeader>
                    <VoiceRecorder />
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {screen === 'setup' && (
          <SetupScreen onStartWorkout={handleStartWorkout} />
        )}
        {screen === 'timer' && (
          <TimerScreen
            config={workoutConfig}
            soundEnabled={soundEnabled}
            onComplete={handleWorkoutComplete}
            onExit={handleGoHome}
            onTimerStateChange={(state) => {
              setActiveTimerState(state);
              setTimerWasActive(state && state.phase !== 'complete');
            }}
          />
        )}
        {screen === 'history' && (
          <HistoryScreen
            history={workoutHistory}
            onBack={() => setScreen('setup')}
          />
        )}
      </main>

    </div>
  );
}

// Error Boundary Component
interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0a0e14] flex items-center justify-center p-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-[#a8abb3] mb-4">Please try refreshing the page</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ExerciseProvider>
          <SyncProvider>
            <ThemeProvider>
              {/* Suspense boundary wraps the entire app so any lazy-loaded
                  route can stream in without blocking the rest. */}
              <Suspense fallback={<RouteLoadingFallback />}>
                <AppContent />
              </Suspense>
              <Toaster />
              {/* iter355 — tells the user when something they logged with no
                  signal is still waiting to reach the cloud. Tap to retry. */}
              <OfflineQueueBanner />
              <CookieBanner />
            </ThemeProvider>
          </SyncProvider>
        </ExerciseProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
